import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listFactors, FACTORS_QUERY_KEY } from '../api/mfa';
import { isUsableFactor } from '../api/types';
import { ApiError } from '../api/client';
import { ME_QUERY_KEY, meQueryOptions } from '../lib/meQuery';
import { provisionAccount } from '../api/logins';
import { useAccessToken, useIdToken, useAuth, useSessionProvenance } from '../auth/useAuth';
import { mfaEnrollmentEnabled, socialLoginEnabled } from '../lib/appEnv';
import { SUPPORT_EMAIL } from '../lib/support';
import { OnboardingFlow } from './OnboardingFlow';
import { FullPageLoading } from '../components/FullPageLoading';

/**
 * Sits above `EnrollmentGate` (identity-scoped, same reasoning: no workspace
 * exists yet to scope requests to) and decides between three first-run
 * outcomes before anything else in the authenticated app renders:
 *
 * 1. **No platform identity yet** (contract: "SPA has tokens but 401s on
 *    `/me`, no IDLINK"). Which disposition applies is decided by the SESSION'S
 *    PROVENANCE, never by the 401 alone (#566) — the two provenances have two
 *    different first-run paths and only one of them can succeed per user:
 *
 *    - **Federated** (a brand-new Google-first signup) — mint the identity via
 *      `POST /me/provision`, then re-check.
 *    - **Native** (email/password) — a dead end, said honestly. A healthy
 *      native signup is provisioned by appcore's PostConfirmation trigger
 *      before the SPA ever loads, so a native session reaching here is one that
 *      trigger could NOT provision: it logs `IDENTITY_PROVISIONING_COLLISION`
 *      (an `EMAIL#` record for this address already resolves to a different
 *      userId, so the whole TransactWriteItems cancels) and deliberately does
 *      not rethrow, because a retry replays the same collision and never
 *      converges. Cognito has the user CONFIRMED; appcore has no USR; there is
 *      no self-service way out. So the only true thing to say is "contact us".
 *
 *      This branch is what makes that trigger's stated assumption true. It says
 *      the user "is surfaced a clear error by the SPA on their next API call
 *      (fail-closed 401)" — but that 401 used to route them into
 *      `/me/provision`, which refuses every natively issued token
 *      `NOT_A_FEDERATED_PROOF` (a hub-brokering defence), which had no UI branch
 *      and rendered "try again". The one refusal a native user was guaranteed to
 *      get was the one that told them to do the one thing that cannot work.
 * 2. **Brand-new founder** (zero usable factors AND zero memberships AND an
 *    unconsumed eligibility grant, whether they arrived here via (1) or via
 *    native email/password signup) — the unified {@link OnboardingFlow}.
 *
 *    A pending invitation no longer disqualifies them (#433). It used to: the
 *    condition required `invites.length === 0`, so someone granted their own
 *    tenant who was ALSO invited to a relative's workspace got neither this flow
 *    nor the founder-arrival screen and landed on the bare picker — the user with
 *    the most going on got the thinnest onboarding. Holding an invitation says
 *    nothing about whether you should be walked through setting up your own org,
 *    and step 1 (enrolling a factor) is required for either path anyway. The flow
 *    offers them an explicit way out to the invitation instead.
 *
 *    The takeover stays strictly MEMBER-LESS. Someone who already belongs to a
 *    workspace and happens to hold a grant must never be hijacked into org
 *    creation on login — they came to use a workspace. Their affordance is the
 *    picker's "Create organization" button (SelectWorkspace).
 * 3. Anything else (an existing member re-enrolling a lost factor, an
 *    invited-but-not-yet-a-member user with no grant of their own, an
 *    already-set-up user) — pass through unchanged to
 *    `EnrollmentGate`/`WorkspaceProvider`, preserving every existing behavior.
 *
 * Gated by the same `mfaEnrollmentEnabled()` flag as `EnrollmentGate` — if
 * MFA enrollment isn't turned on for this build, this is a pass-through and
 * the OLD founder-arrival + bootstrap-modal flow (SelectWorkspace.tsx) is
 * what a member-less user sees, exactly as before this feature.
 */
/**
 * What `GET /me` answers when the caller's token verifies but resolves to no
 * IDLINK — appcore `packages/core/src/auth/identity-resolver.ts`, forwarded
 * code-and-all by the façade (`err.upstreamCode ?? err.code`). It is the ONE
 * 401 that means "this session has no platform identity" rather than "this
 * session is over", and only the first of those has a first-run disposition.
 */
const IDENTITY_NOT_LINKED = 'IDENTITY_NOT_LINKED';

export function FirstRunGate({ children }: { children: ReactNode }) {
  const enabled = mfaEnrollmentEnabled();
  const token = useAccessToken();
  const provenance = useSessionProvenance();
  const queryClient = useQueryClient();
  /**
   * The user chose to go accept a pending invitation instead of creating their own
   * org. This MUST be gate-local state rather than a navigation: this gate wraps the
   * whole `/` route tree INCLUDING `/select-workspace` (router.tsx), so linking there
   * would re-run the takeover and re-render the flow — the same loop that made
   * `/select-workspace` an explicit exemption in `AssuranceFloorGate`. Stepping aside
   * renders children, and `WorkspaceProvider` routes a member-less user to the picker
   * on its own.
   *
   * Deliberately not persisted: it is only needed for the window before they accept,
   * and accepting gives them a membership, which ends the takeover for good.
   */
  const [steppedAside, setSteppedAside] = useState(false);

  /**
   * True from the moment a workspace bootstrap's completion callback fires until
   * BOTH refetches below have landed (steward-app#1642 rework — see
   * {@link finishOnboarding}'s own doc for the mechanism this closes). While true,
   * the render below is held on `OnboardingFlow` regardless of what `hasFactor`
   * or `isBrandNewFounder` say individually, which is what makes the fix
   * ORDER-INDEPENDENT: it does not matter which of the two queries' notifications
   * React processes first, because neither alone can flip this gate open.
   *
   * ⚠️ "Regardless" also has to cover `steppedAside`, not just the two query-derived
   * values above — a second independent-review finding, steward-app#1642 follow-up.
   * `steppedAside` is OR'd into this same condition below (`... && !steppedAside`),
   * so it can reopen exactly the same door: were it able to flip true while
   * `bootstrapping` is also true, `children` would render — `WorkspaceProvider`
   * included — on whatever `/me` is currently cached, which during this window is
   * still the stale, pre-bootstrap payload. The reason it CANNOT flip during
   * bootstrap is not anything in this file: `steppedAside` only ever changes from
   * `OnboardingFlow`'s "Go to your invitation instead" link (`Step2` in
   * `OnboardingFlow.tsx`), and that control is now `disabled` for exactly the
   * `submitting` span, which starts before `finishOnboarding` is even called and
   * does not clear until `onComplete` — this component's own `finishOnboarding` —
   * resolves, i.e. a superset of `bootstrapping`'s own true span. See that button's
   * doc in `OnboardingFlow.tsx` for the guard itself, and
   * `OnboardingFlow.test.tsx`'s "does not offer the invitation escape while a
   * submit is in flight" case (under "step 2: create your space") for the test
   * that pins it — disabling a control in a DIFFERENT component to keep a boolean
   * in THIS one meaningful is exactly the kind of cross-file invariant that drifts
   * silently, so treat a change to either side as a change to both.
   */
  const [bootstrapping, setBootstrapping] = useState(false);

  const factorsQuery = useQuery({
    queryKey: FACTORS_QUERY_KEY,
    queryFn: () => listFactors(token!),
    enabled: enabled && !!token,
    staleTime: 60_000,
    retry: 2,
  });

  const meQuery = useQuery(meQueryOptions(token, enabled));

  /**
   * The shared re-check both first-run changes (a re-provisioned identity, or a
   * completed workspace bootstrap) need: refetch this gate's own `/me` and
   * `/me/factors`, which is what makes the render condition below re-evaluate
   * against fresh data on the next render.
   *
   * ⚠️ **`{ exact: true }` on the `['me']` call is load-bearing, not decorative**
   * (independent review, steward-app#1642). `FACTORS_QUERY_KEY` is `['me',
   * 'factors']`, and TanStack Query's default `invalidateQueries` filter does a
   * PREFIX match — `queryKey: ['me']` without `exact` already matches `['me',
   * 'factors']` too. Without `exact: true` here, the very first call below would
   * silently invalidate-and-refetch BOTH queries by itself, and the second,
   * explicit `FACTORS_QUERY_KEY` call would then race a SECOND refetch of the
   * same query against the first — two independent fetches for one key, landing
   * in whichever order the network returns them. `exact: true` makes this
   * function issue exactly one refetch per query, so `Promise.all` below reports
   * "both queries refetched once each", not "one fetch happened to satisfy two
   * invalidation requests and a second one raced it".
   */
  function refetchFirstRunQueries(): Promise<void> {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY, exact: true }),
      queryClient.invalidateQueries({ queryKey: FACTORS_QUERY_KEY }),
    ]).then(() => undefined);
  }

  /**
   * `ProvisionGate`'s `onProvisioned` — re-checks after a factor is (re-)linked
   * to a newly-provisioned identity. No render gating needed here the way
   * {@link finishOnboarding} below needs one: this path is reached only while
   * `meQuery` itself is still erroring 401 (the branch below that renders
   * `ProvisionGate` in the first place), so whichever of the two refetches
   * lands first, the gate stays on `ProvisionGate` until `meQuery`'s OWN error
   * clears — there is no `isBrandNewFounder`/`hasFactor` window for a stale
   * `factorsQuery` to fall through prematurely the way OnboardingFlow's
   * completion has.
   */
  function refreshAfterFirstRunChange(): Promise<void> {
    return refetchFirstRunQueries();
  }

  /**
   * `OnboardingFlow`'s `onComplete` (steward-app#1642). `Step2.submit` awaits
   * this before navigating, but that alone does not close the bug: awaiting a
   * `Promise.all` of two `invalidateQueries` calls only guarantees the CACHE
   * carries fresh data once both have settled — it says nothing about what THIS
   * component rendered in between. `factorsQuery` and `meQuery` are two
   * independent `useQuery` observers, and TanStack Query notifies each one's
   * subscribers the moment ITS OWN query settles, not once every in-flight
   * refetch this render cycle started has. If the factors refetch happens to
   * land before the `/me` refetch, React re-renders this component with
   * `hasFactor` now `true` while `meQuery.data` is STILL the stale,
   * pre-bootstrap payload (`memberships: []`) — and the render condition below,
   * read literally, would fall through to `children` right there: `!hasFactor`
   * is now `false`, so the whole `if` is `false` regardless of `isBrandNewFounder`
   * still (wrongly) reading true off the stale `/me`. `WorkspaceProvider` would
   * then mount on that same stale `/me` and its own member-less-redirect effect
   * (`WorkspaceProvider.tsx`) would send the brand-new user to
   * `/select-workspace` before the `/me` refetch — already in flight — ever
   * lands to correct the picture. Measured directly:
   * `FirstRunGate.workspaceRace.test.tsx`'s "factors resolves before /me" case
   * reproduces exactly this against the pre-rework code (the single `await
   * onComplete()` in `OnboardingFlow` alone, without `bootstrapping` below).
   *
   * `bootstrapping` is what actually closes it: set synchronously before either
   * refetch starts, and read directly in the render condition below, it keeps
   * this component returning `OnboardingFlow` through every intermediate render
   * in between, in EITHER settling order, because neither `hasFactor` alone nor
   * `isBrandNewFounder` alone can open the gate while it's true. Only once
   * `refetchFirstRunQueries()` has settled — meaning both queries have
   * genuinely refetched — does clearing it let the render condition run again,
   * and by then `meQuery.data` already carries the new membership.
   */
  async function finishOnboarding(): Promise<void> {
    setBootstrapping(true);
    try {
      await refetchFirstRunQueries();
    } finally {
      setBootstrapping(false);
    }
  }

  if (!enabled) return <>{children}</>;
  if (!token) return null;

  const meError = meQuery.isError && meQuery.error instanceof ApiError ? meQuery.error : null;
  // Tokens the API accepts, but no appcore identity behind them. The ENTRY
  // condition is unchanged (any 401 from `/me`) so no existing route can regress;
  // #566 only splits what happens next.
  if (meError?.status === 401) {
    // Two conditions, both required, because the dead end below is permanent and
    // must never swallow a recoverable session:
    //   - a POSITIVE native reading. `unknown` (claims not readable yet) asks the
    //     server instead, since the server is the authority on provenance and its
    //     refusal now explains itself; only a certain reading takes this path.
    //   - appcore's identity-missing code specifically. A 401 can also mean the
    //     session simply died (an expired token, or API Gateway's own code-less
    //     rejection) — for which "signing in again won't fix it" is false, and
    //     signing in again is the actual fix. Those keep the pre-#566 path, where
    //     ProvisionGate's own 401 lands on the transient copy that says to retry.
    if (provenance === 'native' && meError.code === IDENTITY_NOT_LINKED) {
      return <FirstRunDeadEnd message={SETUP_INCOMPLETE_COPY} />;
    }
    return <ProvisionGate token={token} onProvisioned={refreshAfterFirstRunChange} />;
  }

  if (factorsQuery.isLoading || meQuery.isLoading) {
    return <FullPageLoading />;
  }

  // A factors-list failure here doesn't get its own retry UI — the SAME
  // query (identical key, shared cache) is also owned by EnrollmentGate,
  // mounted just below; passing through lets IT show the fail-closed retry
  // state rather than duplicating that UI here.
  if (factorsQuery.isError) return <>{children}</>;

  const hasFactor = (factorsQuery.data ?? []).some(isUsableFactor);
  const memberships = meQuery.data?.memberships ?? [];
  const invites = meQuery.data?.invites ?? [];
  // The real precondition for the founder path: an unconsumed eligibility grant
  // (appcore#346). Absent — an appcore predating the field — falls back to the old
  // proxy, which combined with the member-less test below reproduces the previous
  // condition exactly, so an older API sees no behaviour change.
  const canBootstrapTenant = meQuery.data?.canBootstrapTenant ?? invites.length === 0;
  const isBrandNewFounder = !meQuery.isError && memberships.length === 0 && canBootstrapTenant;

  // `bootstrapping` first, and OR'd in rather than folded into `isBrandNewFounder`
  // or `hasFactor` individually: see `finishOnboarding`'s doc above for why ONLY a
  // condition that cannot be flipped by either query alone closes the #1642 race.
  if (((!hasFactor && isBrandNewFounder) || bootstrapping) && !steppedAside) {
    return (
      <OnboardingFlow
        onComplete={finishOnboarding}
        // Only offered when there is genuinely somewhere else to go. Passing a
        // handler is what makes the affordance appear, so a founder with no
        // invitation never sees a door that leads nowhere.
        onAcceptInviteInstead={invites.length > 0 ? () => setSteppedAside(true) : undefined}
      />
    );
  }

  return <>{children}</>;
}

/**
 * `403 NOT_ELIGIBLE` on a first Google sign-in: nothing admits this address (no subscription, firm
 * grant or live invitation). The commonest cause is an invitation that has expired, so it says
 * that, to everyone and conditionally, which discloses nothing about the address. The first two
 * sentences are shared word for word with appcore's native sign-up refusal
 * (`services/auth-triggers/src/pre-sign-up.ts`, `SIGNUP_NOT_ADMITTED_MESSAGE`): same fact,
 * different login. Each repo pins the shared text in a test; change both together. Exported for
 * that test.
 */
export const NOT_ELIGIBLE_COPY =
  'You don’t have an active invitation or subscription for this email address. If you were invited, '
  + 'your invitation may have expired, so please ask the person who invited you to send a new one. '
  + `If you believe this is an error, contact us at ${SUPPORT_EMAIL}.`;

/**
 * Said to a user whose Cognito account is confirmed but who has no appcore
 * identity and no way to mint one — the PostConfirmation collision described in
 * the gate's docblock, reached either directly (a native session that 401s) or
 * via `/me/provision` refusing a native token `NOT_A_FEDERATED_PROOF`.
 *
 * Every clause is load-bearing. "Signing in again won't finish it" is the part
 * the old generic copy got wrong: it said "try again", and the stage logs show
 * the user doing exactly that, 30 seconds later, to the same dead end. An
 * operator has to repair the collided `EMAIL#` record; nothing the user can do
 * from here changes the outcome, so support is the only honest next step.
 */
const SETUP_INCOMPLETE_COPY = `We couldn’t finish setting up your account, and signing in again won’t finish it. Please contact us at ${SUPPORT_EMAIL} and we’ll get you in.`;

type ProvisionUiState = 'working' | 'account-exists' | 'not-eligible' | 'not-federated' | 'error';

/**
 * Runs `POST /me/provision` exactly once per mount (a ref latch, not a
 * dependency array — this must never re-fire on an unrelated re-render) and
 * renders the terminal outcome. `account-exists`, `not-eligible` and
 * `not-federated` are dead ends by design (contract: "route the user to sign in
 * + link from Settings" / no self-service eligibility override / a native token
 * can never be a provisioning proof) — the only way forward is signing out and
 * starting over, e.g. with a different login.
 *
 * Reached only for a federated (or not-yet-readable) session: the gate no longer
 * sends a known-native one here, because none of them can succeed (#566).
 */
function ProvisionGate({ token, onProvisioned }: { token: string; onProvisioned: () => void }) {
  const idToken = useIdToken();
  const [state, setState] = useState<ProvisionUiState>('working');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    if (!idToken) {
      setState('error');
      return;
    }
    void (async () => {
      try {
        await provisionAccount(token, { idToken });
        onProvisioned();
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && err.code === 'ACCOUNT_EXISTS') {
          setState('account-exists');
        } else if (err instanceof ApiError && err.status === 403 && err.code === 'NOT_ELIGIBLE') {
          setState('not-eligible');
        } else if (err instanceof ApiError && err.code === 'NOT_A_FEDERATED_PROOF') {
          // Belt and braces behind the provenance split above, which should keep a
          // native token from ever arriving here: whatever route reaches this
          // refusal, it is a native token at a federated-only endpoint, so retrying
          // is guaranteed to fail and must not be suggested. Matched on the code
          // alone, not `401 && code` — the status is appcore's to choose, and
          // silently degrading to "try again" if it ever moves is the exact failure
          // this branch exists to end.
          setState('not-federated');
        } else {
          setState('error');
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === 'working') {
    return <FullPageLoading label="Setting up your account…" />;
  }

  const copy: Record<Exclude<ProvisionUiState, 'working'>, string> = {
    // Point to Settings -> Security only when the Logins section is actually available
    // in this build (socialLoginEnabled). If a pool ever has the Google IdP wired while
    // the SPA flag is off (a transient rollout mis-sync), a `account-exists` federated
    // signup must not be told to visit a section that isn't rendered.
    //
    // The tab NAME matters here: this is the one screen where the user is stuck and this
    // copy is the only instruction they get, so it has to match what Settings.tsx labels
    // the tab. Logins merged into Security (docs/29 F4) and this string was the one place
    // the rename was missed — it sent users hunting for a tab that is not in the nav.
    'account-exists': socialLoginEnabled()
      ? `You already have an account with this email. Sign in with your existing login, then add Google from Settings → Security.`
      : `You already have an account with this email. Sign in with your existing login.`,
    'not-eligible': NOT_ELIGIBLE_COPY,
    'not-federated': SETUP_INCOMPLETE_COPY,
    // Keeps "Try again" deliberately: this is now only the genuinely transient
    // residue (a network failure, a 5xx, a missing ID token), where retrying IS
    // the right advice. Every refusal that can never converge has its own branch
    // above.
    error: `We couldn’t finish setting up your account. Try again, or contact us at ${SUPPORT_EMAIL}.`,
  };

  return <FirstRunDeadEnd message={copy[state]} />;
}

/**
 * The shared terminal screen for a first-run state with no way forward but
 * signing out: one sentence and one door. Shared so the native dead end and
 * ProvisionGate's refusals cannot drift into two different-looking screens for
 * what is, to the user, the same stuck moment.
 */
function FirstRunDeadEnd({ message }: { message: string }) {
  const { signOut } = useAuth();
  return (
    <div className="mx-auto max-w-[420px] pt-[10vh] text-center">
      <p className="text-[14px] leading-relaxed text-navy font-sans">{message}</p>
      <button
        onClick={() => void signOut()}
        className="mt-6 px-5 py-2.5 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans"
      >
        Back to sign in
      </button>
    </div>
  );
}
